import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// UI
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";

// Report Components
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";

// Hooks
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";

// Services
import { apiClient } from "@/lib/apiClient";

// Icons
import { Location, Building, Employees, Percentage } from "@/components/icons";

// Styles
import styles from "./LocationPerformanceReportPage.module.scss";

// ---------------------------------------------------------------------------
// Inline types — backend shape (reports-location-performance.service.ts).
// `attendanceCount` (binary attendance) — saat değil katılım sayısı.
// ---------------------------------------------------------------------------
interface LocationPerformanceItem {
  locationID: string;
  name: string;
  address: string | null;
  userCount: number;
  enrollmentCount: number;
  completedCount: number;
  completionRatePercent: number;
  totalAttendanceCount: number;
}

interface LabelValue {
  label: string;
  value: number;
}
interface LabelCount {
  label: string;
  count: number;
}

interface LocationPerformanceResponse {
  summary: {
    totalLocations: number;
    totalUsers: number;
    totalEnrollments: number;
    avgCompletionRatePercent: number;
    bestLocationName: string | null;
    worstLocationName: string | null;
  };
  byCompletionRate: LabelValue[];
  topByUsers: LabelCount[];
  topByAttendance: LabelCount[];
  items: LocationPerformanceItem[];
  count: number;
}

interface LocationPerformanceParams {
  page?: number;
  limit?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ---------- Local fetcher ----------
const fetchLocationPerformance = async (
  params: LocationPerformanceParams,
  signal?: AbortSignal,
): Promise<LocationPerformanceResponse> => {
  const { data } = await apiClient.get(
    "/api/admin/reports/location-performance",
    { params, signal },
  );
  return (data?.data ?? data) as LocationPerformanceResponse;
};

// ---------- Helpers ----------
const PAGE_SIZE = 20;

const formatNumber = (n: number | null | undefined): string =>
  new Intl.NumberFormat("tr-TR").format(Math.round(Number(n ?? 0)));

const formatPercent = (n: number | null | undefined): string =>
  `${(Number(n ?? 0)).toFixed(1)}%`;

const LocationPerformanceReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: LocationPerformanceParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    LocationPerformanceResponse,
    LocationPerformanceParams
  >({
    fetcher: (p, signal) => fetchLocationPerformance(p, signal),
    params,
  });

  const summary = data?.summary;
  const byCompletionRate = data?.byCompletionRate ?? [];
  const topByUsers = data?.topByUsers ?? [];
  const topByAttendance = data?.topByAttendance ?? [];
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const columns: TableColumn<LocationPerformanceItem>[] = [
    {
      key: "name",
      label: t("reports.locationPerformance.headers.location", "LOKASYON"),
      header: t("reports.locationPerformance.headers.location", "LOKASYON"),
      accessor: (row) => (
        <span className={styles.titleCell}>{row.name || "—"}</span>
      ),
    },
    {
      key: "address",
      label: t("reports.locationPerformance.headers.address", "ADRES"),
      header: t("reports.locationPerformance.headers.address", "ADRES"),
      accessor: (row) => (
        <span className={styles.subText}>{row.address || "—"}</span>
      ),
    },
    {
      key: "userCount",
      label: t("reports.locationPerformance.headers.staff", "PERSONEL"),
      header: t("reports.locationPerformance.headers.staff", "PERSONEL"),
      accessor: (row) => (
        <span className={styles.numCell}>{formatNumber(row.userCount)}</span>
      ),
    },
    {
      key: "enrollmentCount",
      label: t("reports.locationPerformance.headers.enrollment", "KAYIT"),
      header: t("reports.locationPerformance.headers.enrollment", "KAYIT"),
      accessor: (row) => (
        <span className={styles.numCell}>
          {formatNumber(row.enrollmentCount)}
        </span>
      ),
    },
    {
      key: "completedCount",
      label: t("reports.locationPerformance.headers.completed", "TAMAMLANAN"),
      header: t("reports.locationPerformance.headers.completed", "TAMAMLANAN"),
      accessor: (row) => (
        <span className={styles.numCell}>
          {formatNumber(row.completedCount)}
        </span>
      ),
    },
    {
      key: "completionRatePercent",
      label: t(
        "reports.locationPerformance.headers.completionRate",
        "TAMAMLAMA %",
      ),
      header: t(
        "reports.locationPerformance.headers.completionRate",
        "TAMAMLAMA %",
      ),
      accessor: (row) => {
        const v = row.completionRatePercent;
        const cls =
          v >= 75
            ? styles.ratioGood
            : v >= 40
              ? styles.ratioMid
              : styles.ratioBad;
        return <span className={cls}>{formatPercent(v)}</span>;
      },
    },
    {
      key: "totalAttendanceCount",
      label: t("reports.locationPerformance.headers.attendance", "KATILIM"),
      header: t("reports.locationPerformance.headers.attendance", "KATILIM"),
      accessor: (row) => (
        <span className={styles.numCell}>
          {formatNumber(row.totalAttendanceCount)}
        </span>
      ),
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<LocationPerformanceItem>({
    reportKey: "LOCATION_PERFORMANCE",
    reportTitle: t("reports.locationPerformance.title", "Lokasyon Performansı"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.locationPerformance.title", "Lokasyon Performansı")}
      layoutKey={`report:LOCATION_PERFORMANCE`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t(
        "reports.locationPerformance.category",
        "Demografi & Lokasyon",
      )}
      description={t(
        "reports.locationPerformance.description",
        "Lokasyon bazlı personel sayısı, kayıt, tamamlama oranı ve yoklama katılımı.",
      )}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="LOCATION_PERFORMANCE"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="lokasyon-performansi"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
        />
      }
    >
      {/* KPI grid */}
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t(
            "reports.locationPerformance.kpi.activeLocations",
            "Aktif Lokasyon",
          )}
          value={summary ? formatNumber(summary.totalLocations) : "—"}
          icon={<Location width={18} height={18} />}
        />
        <ReportKPICard
          title={t(
            "reports.locationPerformance.kpi.totalUsers",
            "Toplam Kullanıcı",
          )}
          value={summary ? formatNumber(summary.totalUsers) : "—"}
          icon={<Employees width={18} height={18} />}
        />
        <ReportKPICard
          title={t(
            "reports.locationPerformance.kpi.avgCompletion",
            "Ort. Tamamlama %",
          )}
          value={
            summary ? formatPercent(summary.avgCompletionRatePercent) : "—"
          }
          icon={<Percentage width={18} height={18} />}
          accent={
            summary && summary.avgCompletionRatePercent >= 75
              ? "success"
              : summary && summary.avgCompletionRatePercent >= 40
                ? "warning"
                : "danger"
          }
        />
        <ReportKPICard
          title={t(
            "reports.locationPerformance.kpi.bestLocation",
            "En İyi Lokasyon",
          )}
          value={summary?.bestLocationName ?? "—"}
          icon={<Building width={18} height={18} />}
          accent="success"
          footnote={
            summary?.worstLocationName
              ? t(
                  "reports.locationPerformance.kpi.worstLocationNote",
                  "En düşük: {{name}}",
                  { name: summary.worstLocationName },
                )
              : undefined
          }
        />
      </div>

      {/* Charts */}
      <div className={styles.chartGrid}>
        <ReportChart
          title={t(
            "reports.locationPerformance.charts.byCompletion",
            "Top 10 Lokasyon Tamamlama %",
          )}
          type="bar"
          isLoading={isLoading}
          isEmpty={byCompletionRate.length === 0}
          height={320}
          series={[
            {
              name: t(
                "reports.locationPerformance.series.completionRate",
                "Tamamlama %",
              ),
              data: byCompletionRate.map((b) => b.value),
            },
          ]}
          options={{
            chart: { toolbar: { show: false } },
            xaxis: { categories: byCompletionRate.map((b) => b.label) },
            colors: ["#10b981"],
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            dataLabels: {
              enabled: true,
              formatter: (val: number) => `${val.toFixed(1)}%`,
            },
            yaxis: { max: 100 },
          }}
        />

        <ReportChart
          title={t(
            "reports.locationPerformance.charts.byUsers",
            "En Çok Personelli Top 10",
          )}
          type="bar"
          isLoading={isLoading}
          isEmpty={topByUsers.length === 0}
          height={320}
          series={[
            {
              name: t(
                "reports.locationPerformance.series.userCount",
                "Personel",
              ),
              data: topByUsers.map((b) => b.count),
            },
          ]}
          options={{
            chart: { toolbar: { show: false } },
            xaxis: { categories: topByUsers.map((b) => b.label) },
            colors: ["#4f46e5"],
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            dataLabels: { enabled: false },
          }}
        />

        <ReportChart
          title={t(
            "reports.locationPerformance.charts.byAttendance",
            "Yoklama Katılımı Top 10",
          )}
          type="bar"
          isLoading={isLoading}
          isEmpty={topByAttendance.length === 0}
          height={320}
          series={[
            {
              name: t(
                "reports.locationPerformance.series.attendanceCount",
                "Katılım",
              ),
              data: topByAttendance.map((b) => b.count),
            },
          ]}
          options={{
            chart: { toolbar: { show: false } },
            xaxis: { categories: topByAttendance.map((b) => b.label) },
            colors: ["#f59e0b"],
            plotOptions: {
              bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
            },
            dataLabels: { enabled: false },
          }}
        />
      </div>

      {/* Items table */}
      {cfg.columnSettings}
      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.locationID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t(
                "reports.locationPerformance.errorLoading",
                "Veri yüklenemedi.",
              )
            : t(
                "reports.locationPerformance.noItems",
                "Lokasyon kaydı bulunamadı.",
              )
        }
      />
    </ReportDetailLayout>
  );
};

export default LocationPerformanceReportPage;
